import { Copy, Download, FileText } from "lucide-react";
import React, { useState } from "react";
import voucherService from "../../redux/services/voucher.service";
import VoucherHeader from "./VoucherHeader";
import { useDispatch, useSelector } from "react-redux";
import { retrieveVouchersList } from "../../redux/actions/voucher";
import GenerateVoucher from "./GenerateVoucher";
import { Slide, toast } from "react-toastify";
// import { formatDate } from "../../utils";
import { checkValidation, formatMessageTime } from "../../helpers/Helper";
import DataTable from "react-data-table-component";
import CustomLoaderComponent from "../Common/Loader/Loader";
export default function Voucher() {
  // State
  const { voucherData, loading } = useSelector((state) => state.voucher);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("generated");
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(10);
  const [toasts, setToasts] = useState([]);
  const dispatch = useDispatch();
  React.useEffect(() => {
    dispatch(
      retrieveVouchersList({
        page,
        perPage,
        isActive: activeTab === "generated" ? true : false,
      })
    );
  }, [dispatch, page, perPage, activeTab]);
  // Mock voucher history data
  // Functions
  const generateVouchers = async (numberOfVouchers, voucherValue, prefix) => {
    try {
      setIsGenerating(true);
      const res = await voucherService.createVoucher({
        amount: voucherValue,
        numOfVoucher: numberOfVouchers,
        codePrefix: prefix,
      });
      if (res.status === 200) {
        toast.success(res.data.message, {
          transition: Slide,
          closeButton: true,
          autoClose: 3000,
          position: "top-right",
          type: "success",
        });
        dispatch(
          retrieveVouchersList({
            page,
            perPage: numberOfVouchers,
            isActive: activeTab === "generated" ? true : false,
          })
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred", {
        transition: Slide,
        closeButton: true,
        autoClose: 3000,
        position: "top-right",
        type: "error",
      });
    } finally {
      setIsGenerating(false);
    }
    // Simulate API call delay
    // setTimeout(() => {
    //   const newVouchers = [];
    //   const today = new Date().toISOString().split("T")[0];

    //   for (let i = 0; i < numberOfVouchers; i++) {
    //     // Generate random code
    //     const randomPart1 = Math.random()
    //       .toString(36)
    //       .substring(2, 6)
    //       .toUpperCase();
    //     const randomPart2 = Math.random()
    //       .toString(36)
    //       .substring(2, 6)
    //       .toUpperCase();

    //     newVouchers.push({
    //       code: `${prefix}-${randomPart1}-${randomPart2}`,
    //       value: voucherValue,
    //       created: today,
    //       status: "Active",
    //     });
    //   }

    //   setGeneratedVouchers(newVouchers);
    //   setIsGenerating(false);

    //   showToast(
    //     "Vouchers Generated",
    //     `Successfully generated ${numberOfVouchers} vouchers.`
    //   );
    // }, 1000);
  };

  const copyToClipboard = () => {
    const voucherText = voucherData?.data?.map((v) => v.code).join("\n");
    navigator.clipboard.writeText(voucherText);

    showToast(
      "Copied to Clipboard",
      `${voucherData?.data?.length} voucher codes copied to clipboard.`
    );
  };

  const downloadCSV = () => {
    const headers = ["Code", "Value", "Created", "Status"];
    const csvContent = [
      headers.join(","),
      ...voucherData?.data?.map((v) =>
        [
          v.code,
          v.amount,
          v.createdAt,
          v.isActive ? "active" : "Inactive",
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `vouchers_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showToast("Download Started", "Your vouchers CSV file is downloading.");
  };

  const showToast = (title, message) => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, title, message }]);

    // Auto-remove toast after 3 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 3000);
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    let className = "";

    switch (status) {
      case "Active":
        className = "badge-active";
        break;
      case "Redeemed":
        className = "badge-redeemed";
        break;
      case "Expired":
        className = "badge-expired";
        break;
      default:
        className = "badge-default";
    }

    return <span className={`badge ${className}`}>{status}</span>;
  };

  const columns = React.useMemo(() => {
    const commonColumns = [
      {
        name: "Voucher Code",
        selector: (row) => checkValidation(row.code),
        width: "300px",
        headerStyle: { textAlign: "center" },
      },
      {
        name: "Value",
        width: "150px",
        selector: (row) => checkValidation(row.amount),
        headerStyle: { textAlign: "center" },
      },
      {
        name: "Status",
        width: "150px",
        cell: (row) => (
          <div style={{ textAlign: "center" }}>
            {renderStatusBadge(row?.isActive ? "Active" : "Redeemed")}
          </div>
        ),
        headerStyle: { textAlign: "center" },
      },
      {
        name: "Created ",
        headerStyle: {
          textAlign: "right",
        },
        selector: (row) => checkValidation(row.createdAt),
        cell: (row) => (
          <>
            {checkValidation(row.createdAt) !== "N/A"
              ? formatMessageTime(row.createdAt)
              : "N/A"}
          </>
        ),
      },
    ];

    return [...commonColumns];
  }, []);

  const handlePageChange = (page) => {
    setPage(page);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
  };
  return (
    <div className="voucher-generator">
      {/* Main Content */}
      <main className="main-content">
        <VoucherHeader
          totalVouchers={voucherData?.totalVoucher}
          availableVouchers={voucherData?.activeVouchers}
          redeemedValue={voucherData?.redeemedValue}
        />
        {/* Main Grid */}
        <div className="main-grid">
          {/* Generator Form */}
          <GenerateVoucher
            generateVouchers={generateVouchers}
            isGenerating={isGenerating}
          />
          {/* Vouchers Table */}
          <div className="vouchers-section">
            <div className="tabs">
              <button
                className={`tab-button ${
                  activeTab === "generated" ? "active" : ""
                }`}
                onClick={() => setActiveTab("generated")}
              >
                Generated Vouchers
              </button>
              <button
                className={`tab-button ${
                  activeTab === "history" ? "active" : ""
                }`}
                onClick={() => setActiveTab("history")}
              >
                Voucher History
              </button>
            </div>

            {voucherData?.data?.length > 0 && activeTab === "generated" && (
              <div className="action-buttons">
                <button onClick={copyToClipboard} className="action-btn">
                  <Copy className="icon" />
                  Copy All
                </button>
                <button onClick={downloadCSV} className="action-btn">
                  <Download className="icon" />
                  Export CSV
                </button>
              </div>
            )}

            <div className="card">
              <div className="card-content no-padding">
                {activeTab === "generated" && (
                  <>
                    {voucherData?.data?.length === 0 ? (
                      <div className="empty-state">
                        <FileText className="empty-icon" />
                        <h3 className="empty-title">No vouchers generated</h3>
                        <p className="empty-description">
                          Use the form on the left to generate new vouchers.
                          They will appear here.
                        </p>
                      </div>
                    ) : (
                      // <div className="table-container">
                      //   <table className="table">
                      //     <thead>
                      //       <tr>
                      //         <th>Voucher Code</th>
                      //         <th>Value</th>
                      //         <th>Created</th>
                      //         <th>Status</th>
                      //       </tr>
                      //     </thead>
                      //     <tbody>
                      //       {voucherData?.data.map((voucher, index) => (
                      //         <tr key={voucher.HuHY7165}>
                      //           <td className="monospace">{voucher.code}</td>
                      //           <td>€{voucher.amount}</td>
                      //           <td>{formatDate(voucher.createdAt)}</td>
                      //           <td>
                      //             {renderStatusBadge(
                      //               voucher.isActive ? "Active" : "Redeemed"
                      //             )}
                      //           </td>
                      //         </tr>
                      //       ))}
                      //     </tbody>
                      //   </table>
                      // </div>
                      <>
                        <DataTable
                          className="rdt_Wrapper"
                          columns={columns}
                          data={voucherData?.data}
                          pagination
                          paginationServer
                          progressPending={loading}
                          progressComponent={
                            <CustomLoaderComponent width="10vw" height="50vh" />
                          }
                          paginationTotalRows={voucherData?.pagination?.total}
                          paginationDefaultPage={page}
                          paginationPerPage={perPage}
                          onChangeRowsPerPage={handlePerRowsChange}
                          onChangePage={handlePageChange}
                          // subHeader
                          // subHeaderComponent={subHeaderComponent}
                          // paginationRowsPerPageOptions={paginationOptions}
                        />
                      </>
                    )}
                  </>
                )}

                {activeTab === "history" && (
                  // <div className="table-container">
                  //   <table className="table">
                  //     <thead>
                  //       <tr>
                  //         <th>Voucher Code</th>
                  //         <th>Value</th>
                  //         <th>Created</th>
                  //         <th>Status</th>
                  //       </tr>
                  //     </thead>

                  //     <tbody>
                  //       {voucherData?.data.map((voucher, index) => (
                  //         <tr key={voucher.HuHY7165}>
                  //           <td className="monospace">{voucher.code}</td>
                  //           <td>€{voucher.amount}</td>
                  //           <td>{formatDate(voucher.createdAt)}</td>
                  //           <td>
                  //             {renderStatusBadge(
                  //               voucher.isActive ? "Active" : "Redeemed"
                  //             )}
                  //           </td>
                  //         </tr>
                  //       ))}
                  //     </tbody>
                  //   </table>
                  // </div>
                  <DataTable
                    className="rdt_Wrapper"
                    columns={columns}
                    data={voucherData?.data}
                    pagination
                    paginationServer
                    paginationTotalRows={voucherData?.pagination?.total}
                    progressPending={loading}
                    progressComponent={
                      <CustomLoaderComponent width="10vw" height="50vh" />
                    }
                    paginationDefaultPage={page}
                    paginationPerPage={perPage}
                    onChangeRowsPerPage={handlePerRowsChange}
                    onChangePage={handlePageChange}
                    // subHeader
                    // subHeaderComponent={subHeaderComponent}
                    // paginationRowsPerPageOptions={paginationOptions}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Toast Notifications */}
      <div className="toast-container">
        {toasts.map((toast) => (
          <div key={toast.id} className="toast">
            <div className="toast-header">{toast.title}</div>
            <div className="toast-body">{toast.message}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
